import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Menu, X, Gauge, Sparkles, Check, Star, Zap, Building2, CalendarDays, TrendingDown } from "lucide-react";
import Speedometer from "./Speedometer";
import { Logo } from "./Logo";

const FEATURES = [
  { icon: Gauge, t: "See how your brand compares with local rivals" },
  { icon: Sparkles, t: "Get intelligent insights on what to post next" },
  { icon: CalendarDays, t: "Schedule it — or let Growth manage it for you" },
];

const NAV = [["how", "How it works"], ["ladder", "The ladder"], ["pricing", "Pricing"]];

const VARIANTS = {
  1: { bg: "linear-gradient(135deg,#0B1A07 0%,#1C3A12 55%,#0B1A07 100%)", glow: "rgba(147,192,32,.30)", round: "rounded-b-[2.25rem]", features: "bullets" },
  2: { bg: "radial-gradient(120% 120% at 78% 18%, #2C5717 0%, #0A1507 62%)", glow: "rgba(182,225,79,.28)", round: "", features: "chips" },
  3: { bg: "linear-gradient(160deg,#0E2109 0%,#274C1B 100%)", glow: "rgba(147,192,32,.22)", round: "rounded-b-[2.5rem]", features: "cards", browser: true },
  4: { bg: "linear-gradient(135deg,#12280B 0%,#39691E 130%)", glow: "rgba(182,225,79,.35)", round: "rounded-b-[3rem]", features: "checks", lime: true },
};

const SCORES = [74, 68, 79, 71, 83];
const NOTES = [
  { t: "-9 this week", s: "Stories went quiet" },
  { t: "-6 this week", s: "Posting slowed down" },
  { t: "+11 this week", s: "7 assets published" },
  { t: "-8 this week", s: "Engagement dipped" },
  { t: "+12 this week", s: "Video drove reach" },
];

function statsFor(score) {
  const rank = score >= 80 ? "#2" : score >= 73 ? "#3" : score >= 69 ? "#4" : "#5";
  const content = `${Math.min(98, Math.round(score * 0.95 + 6))}%`;
  const eng = `+${Math.max(4, Math.round((score - 55) * 0.9))}%`;
  return [[rank, "Local rank"], [content, "Content used"], [eng, "Engagement"]];
}

function Visual({ score, delta, up, stats }) {
  // Floating on the dark hero — no enclosing card box.
  return (
    <div className="w-full max-w-md relative z-10 flex flex-col items-center">
      <div className="flex items-center justify-end w-full mb-1 px-1">
        <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white transition-colors duration-500" style={{ background: up ? "rgba(182,225,79,.16)" : "rgba(229,169,61,.18)", border: `1px solid ${up ? "rgba(182,225,79,.28)" : "rgba(229,169,61,.3)"}` }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: up ? "#B6E14F" : "#E5C07B" }} />{up ? "Growing" : "Cooling"}
        </span>
      </div>
      <Speedometer value={score} delta={delta} duration={1900} size={300} dark />
      <div className="mt-11 grid grid-cols-3 gap-2.5 text-center w-full">
        {stats.map(([val, l]) => (
          <div key={l} className="rounded-xl py-3" style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)" }}>
            <div className="text-xl font-extrabold tracking-tighter text-white transition-all duration-500">{val}</div>
            <div className="text-[11px] text-white/50 mt-0.5">{l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function HeroHead({ variant = 1, authed, onCta }) {
  const [open, setOpen] = useState(false);
  const v = VARIANTS[variant] || VARIANTS[1];

  const [i, setI] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setI((x) => (x + 1) % SCORES.length), 3600);
    return () => clearInterval(iv);
  }, []);
  const score = SCORES[i];
  const prev = SCORES[(i - 1 + SCORES.length) % SCORES.length];
  const delta = score - prev;
  const up = delta >= 0;
  const stats = statsFor(score);
  const note = NOTES[i];

  return (
    <section className={`relative w-full overflow-hidden ${v.round}`} style={{ background: v.bg }}>
      <div className="blob" style={{ width: 620, height: 620, top: -220, right: -120, background: `radial-gradient(circle, ${v.glow}, transparent 70%)` }} />
      {v.lime && <div className="absolute top-0 inset-x-0 h-1.5" style={{ background: "var(--grad-green)" }} />}

      <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
        {/* Nav */}
        <div className="flex items-center py-5">
          <Link to="/" className="flex items-center" data-testid="landing-logo"><Logo height={22} bare /></Link>
          <nav className="hidden md:flex items-center gap-7 ml-9 text-sm font-medium text-white/65">
            {NAV.map(([h, l]) => <a key={h} href={`#${h}`} className="hover:text-white transition-colors">{l}</a>)}
          </nav>
          <div className="ml-auto hidden md:flex items-center gap-2">
            <Link to="/login" className="px-3 py-2 rounded-xl text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 transition-all focus-ring" data-testid="landing-signin">Sign in</Link>
            <button onClick={onCta} className="btn-primary focus-ring" data-testid="landing-getstarted">{authed ? "Open dashboard" : "Get started"} <ArrowRight size={15} strokeWidth={2} /></button>
          </div>
          <button className="ml-auto md:hidden p-2 rounded-xl text-white hover:bg-white/10 transition-all focus-ring" onClick={() => setOpen((o) => !o)} data-testid="landing-menu">{open ? <X size={20} /> : <Menu size={20} />}</button>
        </div>
        {open && (
          <div className="md:hidden rounded-2xl mb-4 px-5 py-4 space-y-3" style={{ background: "rgba(255,255,255,.06)" }}>
            {NAV.map(([h, l]) => <a key={h} href={`#${h}`} onClick={() => setOpen(false)} className="block text-white/80">{l}</a>)}
            <button onClick={onCta} className="btn-primary w-full focus-ring">{authed ? "Open dashboard" : "Get started"}</button>
          </div>
        )}

        {/* Hero content */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-14 items-center pt-8 pb-20 sm:pt-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
            <span className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-white" style={{ background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.14)" }}>
              <Building2 size={13} strokeWidth={2.2} color="#B6E14F" /> Built for estate agents
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white leading-[1.04] mt-6">
              Stop <span style={{ color: "#B6E14F" }}>guessing</span> with your agency's social.
            </h1>

            {/* Features per variant */}
            {v.features === "bullets" && (
              <ul className="mt-7 space-y-3">
                {FEATURES.map((f) => (
                  <li key={f.t} className="flex items-center gap-3 text-white/85 text-lg"><span className="w-2 h-2 rounded-full shrink-0" style={{ background: "#B6E14F" }} />{f.t}</li>
                ))}
              </ul>
            )}
            {v.features === "chips" && (
              <div className="mt-7 flex flex-wrap gap-2.5">
                {FEATURES.map((f) => (
                  <span key={f.t} className="inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium text-white" style={{ background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.14)" }}><f.icon size={15} color="#B6E14F" strokeWidth={1.75} />{f.t}</span>
                ))}
              </div>
            )}
            {v.features === "cards" && (
              <div className="mt-7 grid sm:grid-cols-3 gap-3">
                {FEATURES.map((f) => (
                  <div key={f.t} className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.12)" }}>
                    <f.icon size={20} color="#B6E14F" strokeWidth={1.75} />
                    <div className="text-sm text-white/85 mt-2 leading-snug">{f.t}</div>
                  </div>
                ))}
              </div>
            )}
            {v.features === "checks" && (
              <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {FEATURES.map((f) => (
                  <li key={f.t} className="flex items-start gap-2.5 text-white/85"><Check size={18} color="#B6E14F" strokeWidth={2.5} className="mt-0.5 shrink-0" />{f.t}</li>
                ))}
              </ul>
            )}

            <div className="flex flex-wrap items-center gap-3 mt-9">
              <button onClick={onCta} className="btn-primary focus-ring !px-6 !py-3.5 text-base" data-testid="hero-cta">See your Growth Score <ArrowRight size={17} strokeWidth={2} /></button>
              <a href="#pricing" className="inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-base font-medium text-white transition-all focus-ring" style={{ border: "1px solid rgba(255,255,255,.25)" }}>View pricing</a>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-7 text-sm text-white/60">
              <span className="inline-flex items-center gap-2"><Check size={15} color="#B6E14F" strokeWidth={2.5} /> No setup fees</span>
              <span className="inline-flex items-center gap-2"><Check size={15} color="#B6E14F" strokeWidth={2.5} /> Works with or without NicheCom</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.92, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }} className="relative flex justify-center lg:justify-end">
            <div className="absolute inset-0 -m-16 pointer-events-none" style={{ background: `radial-gradient(58% 48% at 50% 42%, ${v.glow}, transparent 78%)`, filter: "blur(48px)" }} />
            <Visual score={score} delta={delta} up={up} stats={stats} />
            <motion.div key={i} initial={{ opacity: 0, y: -10, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }} className="absolute left-2 sm:left-6 top-14 glass rounded-2xl px-3.5 py-2.5 shadow-[0_14px_34px_rgba(0,0,0,.3)] hidden sm:flex items-center gap-2.5 z-30">
              <span className="grid place-items-center w-8 h-8 rounded-lg shrink-0" style={{ background: up ? "var(--grad-green)" : "linear-gradient(135deg,#E5A93D,#C77F1C)" }}>{up ? <Zap size={15} color="#fff" strokeWidth={2} /> : <TrendingDown size={15} color="#fff" strokeWidth={2} />}</span>
              <div><div className="text-xs font-bold text-ink leading-none">{note.t}</div><div className="text-[10px] text-ink-3 mt-1">{note.s}</div></div>
            </motion.div>
            <motion.div className="absolute -right-3 sm:-right-6 bottom-28 glass rounded-2xl px-3.5 py-2.5 shadow-[0_10px_30px_rgba(0,0,0,.25)] hidden sm:flex items-center gap-2.5 animate-float z-20" style={{ animationDelay: "1.5s" }}>
              <span className="grid place-items-center w-8 h-8 rounded-lg" style={{ background: "#16180F" }}><Star size={15} color="#B6E14F" strokeWidth={2} fill="#B6E14F" /></span>
              <div><div className="text-xs font-bold text-ink leading-none">#3 of 12</div><div className="text-[10px] text-ink-3 mt-1">↑ 2 this month</div></div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
